import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import {
	$addBalances,
	$balances,
	$cursorBalances,
	$hasMoreBalances,
	$isFetchingBalances,
	$setBalances,
	$setCursorBalances,
	$setHasMoreBalances,
	$setIsFetchingBalances,
	type IBalance,
} from '@/entities/balance.entity';
import {
	$balancesFetchKey,
	$balancesListScrollTop,
	$balancesSearchField,
	$balancesSortBy,
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$setBalancesFetchKey,
	$setBalancesListScrollTop,
	onlineStatusToIsOnline,
	toServerBalancesFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import BalanceCard from '../cards/balance-card';
import { LoaderCircleIcon } from 'lucide-react';

const LIMIT = 35;

export default function BalancesList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [balances, addBalances, setBalances] = useUnit([$balances, $addBalances, $setBalances]);
	const [hasMore, setHasMore] = useUnit([$hasMoreBalances, $setHasMoreBalances]);
	const [cursor, setCursor] = useUnit([$cursorBalances, $setCursorBalances]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingBalances, $setIsFetchingBalances]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$balancesSearchField,
		$balancesSortBy,
		$clientsSortDir,
		$balancesListScrollTop,
		$setBalancesListScrollTop,
		$balancesFetchKey,
		$setBalancesFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerBalancesFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: balances.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 100,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useEffect(() => {
		if (isDevMode) return;

		function addBalancesHandler(data: { balances: IBalance[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.balances?.length) {
				addBalances(data.balances);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on('add-balances', addBalancesHandler);

		return () => {
			socket.off('add-balances', addBalancesHandler);
			if (listRef.current) setScrollTop(listRef.current.scrollTop);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setBalances([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit('get-balances', { ...queryPayload, cursor: null });
		}

		if (balances.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [currentFetchKey]);

	useLayoutEffect(() => {
		if (!listRef.current || savedScrollTop === 0 || balances.length === 0) return;
		listRef.current.scrollTop = savedScrollTop;
	}, [balances.length, savedScrollTop]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= balances.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit('get-balances', { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, balances.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !balances.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LoaderCircleIcon className='animate-spin' size={50} />
				</div>
			) : balances.length === 0 ? (
				<div className='text-center mt-30 text-md'>Балансы не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const bal = balances[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<BalanceCard balance={bal} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
